import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Users, Shield, Calendar, Settings } from "lucide-react";
import StatCard from "./StatCard";
import useWorkspaceLogo from "v2/hooks/useWorkspaceLogo";

// Generate a gradient based on workspace name (same as navbar)
const generateGradient = (name) => {
  if (!name) return "linear-gradient(135deg, #E5E7EB 0%, #D1D5DB 100%)";
  const hash = [...name].reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const hue1 = hash % 360;
  const hue2 = (hue1 + 40) % 360;
  return `linear-gradient(135deg, hsl(${hue1}, 65%, 55%), hsl(${hue2}, 65%, 45%))`;
};

// Get initials from workspace name
const getInitials = (name) => {
  if (!name) return "?";
  return name
    .split(" ")
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();
};

const HeroSection = ({ workspace, stats }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { logoUrl } = useWorkspaceLogo(workspace?.id);

  return (
    <div className="px-6 pb-6 pt-5">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        {/* Left: Avatar + Title */}
        <div className="flex items-center gap-3.5">
          {/* Workspace logo or gradient avatar */}
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={workspace.name}
              className="h-11 w-11 rounded-xl object-cover shadow-sm"
            />
          ) : (
            <div
              className="flex h-11 w-11 items-center justify-center rounded-xl text-base font-bold text-white shadow-sm"
              style={{ background: generateGradient(workspace.name) }}
            >
              {getInitials(workspace.name)}
            </div>
          )}

          <h1 className="text-[22px] font-bold leading-tight text-[#111827]">
            {workspace.name}
          </h1>
        </div>

        {/* Right: Settings Button */}
        <div className="flex items-center">
          <button
            onClick={() => navigate(`/v2/${workspace.id}/settings`)}
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#E5E7EB] bg-white text-[#6B7280] transition duration-150 hover:border-[#D1D5DB] hover:bg-[#F9FAFB] hover:text-[#374151]"
            title={t("workspace.overview.hero.settingsTitle")}
          >
            <Settings size={15} />
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4">
        <StatCard
          icon={Users}
          label={t("workspace.overview.hero.totalAthletes")}
          value={stats.totalPlayers}
        />
        <StatCard
          icon={Shield}
          label={t("workspace.overview.hero.totalTeams")}
          value={stats.totalTeams}
        />
        <StatCard
          icon={Calendar}
          label={t("workspace.overview.hero.totalSessions")}
          value={stats.totalSessions}
        />
      </div>
    </div>
  );
};

export default HeroSection;
